import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Check, Sparkles, AlertCircle, ChevronDown } from "lucide-react";
import ServiceCard from "@/components/ServiceCard";
import SelectionPanel from "@/components/SelectionPanel";

/**
 * Step 1 of the Booking Wizard: category pills, service list, add-on
 * customisation, and the live selection panel.
 */
export default function BookingStep1Services({
  services,
  settings,
  selectedServices,
  selectedCategory,
  setSelectedCategory,
  toggleService,
  addOnSelections,
  setAddOnSelections,
  getServiceTotal,
  onNext,
  onSelectLength = null,
  recommendedServices = [],
  clientStatus = null,
  loading = false,
  error = null,
  onRetry = null,
}) {
  const rawCats = [...new Set(services.map(s => s.category || 'Other'))];
  const order = settings.category_order || [];
  const categories = [
    ...order.filter(c => rawCats.includes(c)),
    ...rawCats.filter(c => !order.includes(c)),
  ];
  const catServices = services.filter(s => (s.category || 'Other') === selectedCategory);

  // Master nail lengths from global settings
  const masterLengths = settings.nail_lengths || [];
  // Master nail finishes / add-ons from global settings (only active)
  const masterFinishes = (settings.nail_finishes || []).filter(f => f.active !== false && (f.name || '').trim());

  // Services requiring a length pick but not yet picked
  const missingLengths = selectedServices.filter(
    s => s.length_pricing_enabled && masterLengths.length > 0 && !addOnSelections[s.id]?.length
  );
  const canProceed = missingLengths.length === 0;

  const customisable = selectedServices.filter(
    s => (s.length_pricing_enabled && masterLengths.length > 0)
      || (s.length_pricing_enabled && masterFinishes.length > 0)
      || (s.extras && s.extras.length > 0)
  );

  return (
    <div className="fade-in flex flex-col lg:flex-row gap-6">
      {/* Left: Category pills + service list + add-ons (all stacked) */}
      <div className="flex-1 min-w-0">
        <h2 className="text-2xl font-semibold tracking-tight mb-1" style={{ fontFamily: 'Playfair Display' }}>Choose your services</h2>
        <p className="text-sm text-muted-foreground mb-5">Select one or more treatments</p>

        {/* Welcome back note — last service + verified nail length, so the client
            sees what we have on file before picking again (encourages honesty). */}
        {clientStatus && !clientStatus.is_new && (clientStatus.last_service_name || clientStatus.last_nail_length) && (
          <div data-testid="welcome-back-note" className="mb-5 p-3.5 rounded-2xl bg-muted/40 border border-border flex items-center gap-2 text-sm">
            <Sparkles size={14} className="text-primary shrink-0" />
            <p className="text-foreground">
              Welcome back! Last time you had{' '}
              {clientStatus.last_service_name && <strong>{clientStatus.last_service_name}</strong>}
              {clientStatus.last_service_name && clientStatus.last_nail_length && ' — '}
              {clientStatus.last_nail_length && <strong>{clientStatus.last_nail_length}</strong>}
            </p>
          </div>
        )}

        {/* Proactive recommendation banner — shown when we know client's last-visit cadence */}
        {clientStatus && !clientStatus.is_new && recommendedServices.length > 0 && (
          <div
            data-testid="service-recommendation-banner"
            className="mb-5 p-4 rounded-2xl bg-gradient-to-r from-teal-50 via-cyan-50 to-sky-50 border border-teal-200/70"
          >
            <p className="text-xs font-semibold text-teal-800 flex items-center gap-1.5 mb-2.5">
              <Sparkles size={13} className="text-teal-600" />
              Based on your last visit{' '}
              {clientStatus.days_since_last_appointment} day{clientStatus.days_since_last_appointment !== 1 ? 's' : ''} ago — right for you right now:
            </p>
            <div className="flex flex-wrap gap-2">
              {recommendedServices.map(svc => {
                const isSelected = !!selectedServices.find(s => s.id === svc.id);
                return (
                  <button
                    key={svc.id}
                    data-testid={`recommend-chip-${svc.id}`}
                    onClick={() => {
                      if (!isSelected) {
                        toggleService(svc);
                        setSelectedCategory(svc.category || 'Other');
                      }
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                      isSelected
                        ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                        : 'bg-white text-teal-800 border-teal-300 hover:bg-teal-50 hover:border-teal-400'
                    }`}
                  >
                    {isSelected && <Check size={10} />}
                    {svc.name}
                    <span className={`text-[10px] ${isSelected ? 'text-teal-100' : 'text-teal-600'}`}>
                      £{svc.price.toFixed(2)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Category pills — first 4 visible + dropdown for the rest */}
        {(() => {
          const VISIBLE_COUNT = 4;
          const visibleCats = categories.slice(0, VISIBLE_COUNT);
          const overflowCats = categories.slice(VISIBLE_COUNT);
          const activeIsOverflow = overflowCats.includes(selectedCategory);

          const renderPill = (cat) => {
            const count = services.filter(s => (s.category || 'Other') === cat).length;
            const selectedInCat = selectedServices.filter(ss => (ss.category || 'Other') === cat).length;
            const isActive = selectedCategory === cat;
            return (
              <button
                key={cat}
                data-testid={`category-pill-${cat}`}
                onClick={() => setSelectedCategory(cat)}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-150 border ${
                  isActive
                    ? 'bg-primary text-white border-primary shadow-sm'
                    : 'bg-white text-foreground border-border hover:border-primary/40 hover:bg-primary/5'
                }`}
              >
                {cat}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                  isActive ? 'bg-white/25 text-white' : 'bg-muted text-muted-foreground'
                }`}>
                  {selectedInCat > 0 ? `${selectedInCat}\u2713 / ${count}` : count}
                </span>
              </button>
            );
          };

          return (
            <div className="flex flex-wrap items-center gap-2 mb-5">
              {visibleCats.map(renderPill)}
              {overflowCats.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="category-pill-more"
                      className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-150 border ${
                        activeIsOverflow
                          ? 'bg-primary text-white border-primary shadow-sm'
                          : 'bg-white text-foreground border-border hover:border-primary/40 hover:bg-primary/5'
                      }`}
                    >
                      {activeIsOverflow ? selectedCategory : 'More'}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                        activeIsOverflow ? 'bg-white/25 text-white' : 'bg-muted text-muted-foreground'
                      }`}>
                        {overflowCats.length}
                      </span>
                      <ChevronDown size={14} className={activeIsOverflow ? 'text-white' : 'text-muted-foreground'} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="rounded-xl min-w-[220px]">
                    {overflowCats.map(cat => {
                      const count = services.filter(s => (s.category || 'Other') === cat).length;
                      const selectedInCat = selectedServices.filter(ss => (ss.category || 'Other') === cat).length;
                      const isActive = selectedCategory === cat;
                      return (
                        <DropdownMenuItem
                          key={cat}
                          data-testid={`category-dropdown-${cat}`}
                          onClick={() => setSelectedCategory(cat)}
                          className={`flex items-center justify-between gap-3 cursor-pointer rounded-lg ${
                            isActive ? 'bg-primary/10 text-primary font-semibold' : ''
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            {isActive && <Check size={13} className="text-primary" />}
                            {cat}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-muted text-muted-foreground">
                            {selectedInCat > 0 ? `${selectedInCat}\u2713 / ${count}` : count}
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })()}

        {/* Service list */}
        <div className="space-y-3" data-testid="services-list">
          {loading ? (
            <div className="space-y-3" data-testid="services-loading">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 rounded-xl bg-muted/40 animate-pulse" />
              ))}
              <p className="text-xs text-muted-foreground text-center pt-2">Loading services…</p>
            </div>
          ) : error ? (
            <div
              data-testid="services-error"
              className="rounded-2xl border-2 border-red-200 bg-red-50/60 p-6 text-center"
            >
              <AlertCircle className="mx-auto mb-2 text-red-500" size={28} />
              <p className="text-sm font-semibold text-red-900 mb-1">Couldn't load services</p>
              <p className="text-xs text-red-700/80 mb-4">{error}</p>
              {onRetry && (
                <Button
                  onClick={onRetry}
                  data-testid="services-retry-button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-red-300 text-red-700 hover:bg-red-100"
                >
                  Try again
                </Button>
              )}
            </div>
          ) : (
            <>
              {catServices.map(svc => (
                <ServiceCard
                  key={svc.id}
                  svc={svc}
                  isSelected={!!selectedServices.find(s => s.id === svc.id)}
                  onToggle={() => toggleService(svc)}
                />
              ))}
              {catServices.length === 0 && (
                <p className="text-sm text-muted-foreground py-6 text-center">No services in this category</p>
              )}
            </>
          )}
        </div>

        {/* Add-ons block — stacked below service list in the same column */}
        {customisable.length > 0 && (
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display' }}>Customise your treatment</h3>
          {customisable.map(svc => {
            const sel = addOnSelections[svc.id] || {};
            const isMissingLength = svc.length_pricing_enabled && masterLengths.length > 0 && !sel.length;
            return (
              <Card key={svc.id} className={`p-4 rounded-xl border transition-colors ${isMissingLength ? 'border-red-300 bg-red-50/20' : ''}`}>
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>{svc.name}</p>
                  {isMissingLength && (
                    <span className="text-[10px] font-semibold text-red-500 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">
                      Choose a length
                    </span>
                  )}
                </div>

                {/* Nail Length */}
                {svc.length_pricing_enabled && masterLengths.length > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xs text-muted-foreground">Nail length</p>
                      {!sel.length && (
                        <span className="text-[10px] font-semibold text-red-500 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">
                          Required
                        </span>
                      )}
                    </div>
                    <div className={`flex gap-2 p-2 rounded-xl transition-colors ${!sel.length ? 'bg-red-50/60 border border-red-200' : ''}`}>
                      {masterLengths.map(opt => {
                        const isActive = sel.length === opt.name;
                        return (
                          <button
                            key={opt.name}
                            data-testid={`length-${svc.id}-${opt.name}`}
                            onClick={() => onSelectLength
                              ? onSelectLength(svc, opt)
                              : setAddOnSelections(prev => ({
                                  ...prev,
                                  [svc.id]: { ...prev[svc.id], length: opt.name, lengthUplift: opt.price_uplift || 0 },
                                }))
                            }
                            className={`flex-1 px-3 py-2.5 rounded-xl text-xs font-medium border-2 transition-all ${
                              isActive
                                ? 'border-primary bg-primary/5 text-primary'
                                : !sel.length
                                  ? 'border-red-200 hover:border-primary/30 hover:bg-primary/5'
                                  : 'border-border hover:border-primary/30'
                            }`}
                          >
                            <span className="block">{opt.name}</span>
                            <span className="block text-[10px] mt-0.5 text-muted-foreground">
                              {opt.price_uplift > 0 ? `+£${opt.price_uplift.toFixed(2)}` : 'Included'}
                            </span>
                            {opt.photo_url && (
                              <img
                                src={opt.photo_url}
                                alt={opt.name}
                                className="mt-2 w-full rounded-lg object-cover"
                                style={{ aspectRatio: '1 / 1' }}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Extras */}
                {svc.extras?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Optional extras</p>
                    <div className="space-y-2">
                      {svc.extras.map(extra => {
                        const isOn = (sel.extras || []).some(e => e.name === extra.name);
                        return (
                          <button
                            key={extra.name}
                            data-testid={`extra-${svc.id}-${extra.name}`}
                            onClick={() => {
                              setAddOnSelections(prev => {
                                const current = prev[svc.id] || {};
                                const exList = current.extras || [];
                                const newExtras = isOn
                                  ? exList.filter(e => e.name !== extra.name)
                                  : [...exList, { name: extra.name, price: extra.price }];
                                return { ...prev, [svc.id]: { ...current, extras: newExtras } };
                              });
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs border-2 transition-all ${
                              isOn ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                            }`}
                          >
                            <span className="font-medium">{extra.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">+£{extra.price.toFixed(2)}</span>
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isOn ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                                {isOn && <Check size={10} className="text-white" />}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Finish / Add-ons (global, only on services requiring nail customisation) */}
                {svc.length_pricing_enabled && masterFinishes.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-muted-foreground">Finish / Add-ons <span className="text-muted-foreground/60">(optional)</span></p>
                      {(sel.extras || []).filter(e => masterFinishes.find(f => f.name === e.name)).length > 0 && (
                        <span className="text-[10px] text-primary font-medium">
                          {(sel.extras || []).filter(e => masterFinishes.find(f => f.name === e.name)).length} selected
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`finishes-grid-${svc.id}`}>
                      {masterFinishes.map(fin => {
                        const isOn = (sel.extras || []).some(e => e.name === fin.name);
                        return (
                          <button
                            key={fin.name}
                            type="button"
                            data-testid={`finish-${svc.id}-${fin.name}`}
                            onClick={() => {
                              setAddOnSelections(prev => {
                                const current = prev[svc.id] || {};
                                const exList = current.extras || [];
                                const newExtras = isOn
                                  ? exList.filter(e => e.name !== fin.name)
                                  : [...exList, { name: fin.name, price: fin.price || 0 }];
                                return { ...prev, [svc.id]: { ...current, extras: newExtras } };
                              });
                            }}
                            className={`relative text-left p-3 rounded-xl border-2 transition-all ${
                              isOn ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30 hover:bg-muted/30'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {fin.icon_url ? (
                                <img
                                  src={fin.icon_url}
                                  alt={fin.name}
                                  className="w-12 h-12 rounded-lg object-cover border border-border shrink-0"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center shrink-0">
                                  <Sparkles size={16} className="text-muted-foreground/50" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="font-semibold text-xs truncate">{fin.name}</p>
                                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${isOn ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                                    {isOn && <Check size={10} className="text-white" />}
                                  </div>
                                </div>
                                {fin.description && (
                                  <p className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">{fin.description}</p>
                                )}
                                <p className={`text-[11px] mt-1 font-medium ${isOn ? 'text-primary' : 'text-muted-foreground'}`}>
                                  {fin.price > 0 ? `+£${Number(fin.price).toFixed(2)}` : 'Free'}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
          </div>
        )}
      </div>

      {/* Right: Selection panel only */}
      <SelectionPanel
        selectedServices={selectedServices}
        onRemove={toggleService}
        onNext={onNext}
        getServiceTotal={getServiceTotal}
        canProceed={canProceed}
      />
    </div>
  );
}
